import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ImagePlus, Loader2, Video, X } from 'lucide-react';
import {
  BrandDealStats,
  BrandDispute,
  BrandRatingSummary,
  BrandSubmissionVersion,
  BrandTask,
  BrandTaskBrief,
  abandonTask,
  buildDraftFileUrl,
  getDraftFileTicket,
  getTaskDetail,
  publishProposalTask,
  saveDraft,
  submitCreatorRating,
  submitTask,
  uploadDraftFile,
} from '../../api/payment/brand-deal.api';
import { formatRate } from '../../utils/brand-conversion';
import { contentFormatHint, validateContentFormat } from '../../utils/brand-content-format';
import { BrandRatingBox } from '../../components/brand-deal/brand-rating-form';
import {
  BRAND_SUBMISSION_LIMITS,
  canAbandon,
  countdownLabel,
  creativeRulesLabel,
  rejectReasonLabel,
  taskStatusLabel,
  validateDraft,
} from '../../utils/brand-creative';
import { daysLeftLabel, disputeStatusLabel } from '../../utils/brand-dispute';
import { PUBLISH_CONFIRM_TEXT, publishCountdownText, usesTaskFlow } from '../../utils/brand-proposal';
import { routePaths } from '../../routes/routes-path';
import BrandDisputeForm from '../../components/brand-deal/brand-dispute-form';
import BrandDisputePanel from '../../components/brand-deal/brand-dispute-panel';
import { IndividualOrdererBadge } from '../../components/brand-deal/orderer-badge';
import { BrandThreadRef, getBrandThreadByDeal } from '../../api/payment/brand-thread.api';
import BrandThreadPanel from '../../components/brand-deal/brand-thread-panel';
import { THREAD_FAIRNESS_NOTE } from '../../utils/brand-thread';
import { BRIEF_INVITE_PENDING_NOTE, briefVersionLabel } from '../../utils/brand-brief-invite';

/**
 * Bàn soạn bài (creator, M3 Task 6) — `/collab/task/:dealId`. Trái: brief doanh nghiệp yêu
 * cầu. Phải: ô soạn chữ + tải ảnh/video nháp (tự lưu tối đa 30 giây/lần khi có đổi), "Gửi duyệt",
 * "Bỏ nhiệm vụ". Không còn ở Working/RevisionRequested ⇒ chỉ xem lại (phiên bản đã nộp, ghi chú
 * sửa/lý do từ chối, đồng hồ đếm hạn), không soạn được nữa.
 */

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const NOT_FOUND_MESSAGE = 'Không tìm thấy đơn.';

const AUTOSAVE_INTERVAL_MS = 30000;

const EDITABLE_STATUSES = ['Working', 'RevisionRequested'];

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
// Review M5 (final review): hạ về 100 MB, khớp R14 bên media-service (ne tran 1-request 150MB Dropbox).
const MAX_VIDEO_BYTES = 100 * 1024 * 1024;
const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
// Review M5: bỏ video/x-matroska (.mkv) — media-service không hỗ trợ (VideoExtensions chỉ .mp4/.mov/.webm),
// creator chờ hết cả lượt tải rồi mới nhận "Định dạng file không được hỗ trợ." nếu còn cho chọn ở đây.
const VIDEO_MIME_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
const VIDEO_TOO_LARGE_ERROR = 'Video tối đa 100 MB và 3 phút.';

type AttachmentKind = 'image' | 'video' | 'unknown';

interface Attachment {
  fileId: string;
  kind: AttachmentKind;
}

const formatSavedAt = (d: Date) => d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', hour12: false });

// Khai ngoài component cha (landmine ghi nhớ: component khai trong render body gây remount cả
// subtree, mất trạng thái) — 1 ảnh/video nháp xem qua vé ngắn hạn (5 phút), tự xin vé mới khi hết
// hạn hoặc lúc phần tử media lỗi.
const DraftFileThumb: React.FC<{
  dealId: string;
  fileId: string;
  kind: AttachmentKind;
  onKindResolved: (fileId: string, kind: 'image' | 'video') => void;
  onRemove?: () => void;
}> = ({ dealId, fileId, kind, onKindResolved, onRemove }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [renderKind, setRenderKind] = useState<'image' | 'video'>(kind === 'video' ? 'video' : 'image');
  const [broken, setBroken] = useState(false);
  const expiresAtRef = useRef(0);
  const switchedRef = useRef(false);

  const fetchTicket = useCallback(async () => {
    try {
      const ticket = await getDraftFileTicket(dealId, fileId);
      const absoluteUrl = buildDraftFileUrl(ticket.url);
      expiresAtRef.current = Date.parse(ticket.expiresAt) || 0;
      setUrl(absoluteUrl);
      setBroken(false);
      setRenderKind(ticket.kind);
      if (kind === 'unknown') onKindResolved(fileId, ticket.kind);
    } catch {
      setBroken(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, fileId]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  const handleMediaError = () => {
    // Vé có thể vừa hết hạn ⇒ xin vé mới; nếu còn hạn mà vẫn lỗi (đoán sai loại file) ⇒ thử đổi
    // ảnh/video một lần duy nhất trước khi chịu thua (tránh vòng lặp vô hạn).
    if (Date.now() >= expiresAtRef.current) {
      fetchTicket();
    } else if (!switchedRef.current) {
      switchedRef.current = true;
      setRenderKind((k) => (k === 'image' ? 'video' : 'image'));
    } else {
      setBroken(true);
    }
  };

  return (
    <div className="relative size-24 shrink-0 overflow-hidden rounded-lg border ui-divider bg-slate-100 dark:bg-slate-800">
      {broken || !url ? (
        <div className="flex size-full items-center justify-center text-[11px] ui-muted">
          {broken ? 'Không tải được' : <Loader2 className="animate-spin" size={16} />}
        </div>
      ) : renderKind === 'video' ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
          src={url}
          className="size-full object-cover"
          muted
          playsInline
          preload="metadata"
          onError={handleMediaError}
        />
      ) : (
        <img src={url} alt="" className="size-full object-cover" onError={handleMediaError} />
      )}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white hover:bg-black/80"
        >
          <X size={12} />
        </button>
      )}
    </div>
  );
};

const VersionCard: React.FC<{ version: BrandSubmissionVersion }> = ({ version }) => (
  <div className="ui-card flex flex-col gap-2 p-3">
    <div className="ui-heading text-[13px] font-bold">
      Phiên bản {version.version} · {new Date(version.submittedAt).toLocaleString('vi-VN')}
    </div>
    {version.text && <p className="ui-text whitespace-pre-wrap text-[14px]">{version.text}</p>}
    {version.files.length > 0 && (
      <div className="ui-muted text-[12px]">
        {version.files.length} tệp đính kèm ({version.files.filter((f) => f.kind === 'image').length} ảnh,{' '}
        {version.files.filter((f) => f.kind === 'video').length} video)
      </div>
    )}
  </div>
);

// Khai ngoài component cha (landmine remount) — lời mời làm brief chưa nhận: chỉ đọc brief và trao đổi, trả lời ở tab lời mời (R13).
const BriefInviteNotice: React.FC = () => (
  <div className="flex flex-col gap-2 rounded-lg border border-[#0057FF]/30 bg-[#EAEFFA] p-3 text-[13px] text-[#0b2a6b] dark:bg-slate-800 dark:text-blue-200">
    <p>{BRIEF_INVITE_PENDING_NOTE}</p>
    <Link to={`${routePaths.brandDeals}?tab=invites`} className="self-start font-semibold text-[#0057FF] dark:text-blue-300">
      Mở lời mời
    </Link>
  </div>
);

/** Bàn soạn bài của creator cho brief sáng tạo (giữ nguyên như trước) kèm khung "Trao đổi". */
// Khai ngoài component cha (landmine remount) — khối "Đăng công khai" của đơn nhận đề xuất đã duyệt (spec 2026-10-12 §6).
const ProposalPublishBox: React.FC<{ dueAt?: string | null; nowMs: number; busy: boolean; onPublish: () => void }> = ({
  dueAt,
  nowMs,
  busy,
  onPublish,
}) => (
  <div className="flex flex-col gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-[13px] text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-200">
    <p className="font-semibold">Người đặt đã duyệt bài. Bấm Đăng công khai để kết thúc đơn và nhận tiền.</p>
    {dueAt && <p>{publishCountdownText(dueAt, nowMs)}</p>}
    <button
      type="button"
      disabled={busy}
      onClick={onPublish}
      className="self-start rounded-lg bg-[#0057FF] px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-60"
    >
      {busy ? 'Đang đăng…' : 'Đăng công khai'}
    </button>
  </div>
);

const CreativeTaskWorkspace: React.FC<{ dealId: string; threadId?: string }> = ({ dealId, threadId }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [task, setTask] = useState<BrandTask | null>(null);
  const [brief, setBrief] = useState<BrandTaskBrief | null>(null);
  const [versions, setVersions] = useState<BrandSubmissionVersion[]>([]);
  const [disputeWindowEndsAt, setDisputeWindowEndsAt] = useState<string | null>(null);
  const [dispute, setDispute] = useState<BrandDispute | null>(null);
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [publishing, setPublishing] = useState(false);

  // M5 (Task 3): đánh giá 2 chiều — ô chấm doanh nghiệp + đánh giá đã công bố, hiện khi nhiệm vụ đã xong.
  const [myRating, setMyRating] = useState<BrandRatingSummary | null>(null);
  const [theirRating, setTheirRating] = useState<BrandRatingSummary | null>(null);
  const [ratingDueAt, setRatingDueAt] = useState<string | null>(null);
  // Đo chuyển đổi (spec 2026-10-05-brand-conversion §3) — lượt bấm/chuyển đổi/tỉ lệ của nhiệm vụ này.
  const [stats, setStats] = useState<BrandDealStats | null>(null);
  const [ratingBusy, setRatingBusy] = useState(false);

  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [videoProgress, setVideoProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [abandoning, setAbandoning] = useState(false);
  // Review I4 (final review): submit vừa bị payment từ chối vì video chưa có preview xong (nộp ngay sau
  // khi tải video lên — xem R15 bên media-service, seal-async) — khoá "Gửi duyệt" tới khi có thay đổi
  // tệp đính kèm (thêm/bớt) để tránh bấm lại ngay lập tức rồi lại dính đúng lỗi này.
  const [videoProcessingBlocked, setVideoProcessingBlocked] = useState(false);

  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  const loadedOnceRef = useRef(false);
  const savedSnapshotRef = useRef({ text: '', fileIds: [] as string[] });
  const lastSaveAttemptRef = useRef(0);
  const inFlightSaveRef = useRef<Promise<boolean> | null>(null);
  const videoProcessingBlockedTimerRef = useRef<NodeJS.Timeout | null>(null);

  const load = useCallback(() => {
    if (!dealId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getTaskDetail(dealId)
      .then((detail) => {
        setTask(detail.task);
        setBrief(detail.brief);
        setVersions(detail.versions);
        setDisputeWindowEndsAt(detail.disputeWindowEndsAt);
        setDispute(detail.dispute);
        setMyRating(detail.myRating ?? null);
        setTheirRating(detail.theirRating ?? null);
        setRatingDueAt(detail.ratingDueAt ?? null);
        setStats(detail.stats ?? null);
        const draftText = detail.currentDraft?.text ?? '';
        const draftFileIds = detail.currentDraft?.fileIds ?? [];
        setText(draftText);
        setAttachments(draftFileIds.map((fileId) => ({ fileId, kind: 'unknown' as AttachmentKind })));
        savedSnapshotRef.current = { text: draftText, fileIds: draftFileIds };
        loadedOnceRef.current = true;
      })
      .catch((err) => toast.error(errorMessage(err, 'Không tải được nhiệm vụ.')))
      .finally(() => setLoading(false));
  }, [dealId]);

  const handlePublish = async () => {
    if (!dealId || publishing) return;
    if (!window.confirm(PUBLISH_CONFIRM_TEXT)) return;
    setPublishing(true);
    try {
      const updated = await publishProposalTask(dealId);
      toast.success(updated.message || 'Đã đăng bài, tiền đang về ví của bạn.');
      load();
    } catch (err) {
      toast.error(errorMessage(err, 'Không đăng được bài.'));
      // Việc có thể đã được hệ thống tự đăng: tải lại để khối "Đăng công khai" cũ biến mất.
      load();
    } finally {
      setPublishing(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  // Cập nhật đồng hồ đếm hạn kháng cáo mỗi phút — không cần chính xác tới giây.
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  const canEdit = !!task && EDITABLE_STATUSES.includes(task.status);
  // Fix round 1 (review Important #2): kiểm thêm `task.disputeStatus` (nay đã có ở cả creator) — phòng
  // hờ trường hợp `dispute` chưa kịp nạp nhưng danh sách đã biết có kháng cáo, tránh thoáng hiện lại CTA.
  const canOpenDispute =
    task?.status === 'Rejected' &&
    !dispute &&
    !task?.disputeStatus &&
    !!disputeWindowEndsAt &&
    Date.parse(disputeWindowEndsAt) > nowMs;

  const fileIds = attachments.map((a) => a.fileId);
  const fileIdsKey = fileIds.join(',');

  // Review I4: thêm/bớt tệp đính kèm ⇒ mở khoá lại "Gửi duyệt" (creator đã hành động để thử khắc phục).
  useEffect(() => {
    if (videoProcessingBlockedTimerRef.current) {
      clearTimeout(videoProcessingBlockedTimerRef.current);
      videoProcessingBlockedTimerRef.current = null;
    }
    setVideoProcessingBlocked(false);
  }, [fileIdsKey]);

  // Review I4: khi video đang xử lý khiến nộp bài bị từ chối, tự mở khoá nút sau 60 giây (thay vì chờ
  // creator thêm/bớt tệp). Nếu creator đổi tệp trước 60 giây, effect ở trên sẽ bắt trước.
  useEffect(() => {
    if (!videoProcessingBlocked) return;
    if (videoProcessingBlockedTimerRef.current) {
      clearTimeout(videoProcessingBlockedTimerRef.current);
    }
    videoProcessingBlockedTimerRef.current = setTimeout(() => {
      setVideoProcessingBlocked(false);
      videoProcessingBlockedTimerRef.current = null;
    }, 60000); // 60 seconds
  }, [videoProcessingBlocked]);

  /** Lưu nháp thật sự — luôn gửi `text`/`fileIds` TẠI THỜI ĐIỂM GỌI. */
  const performSave = useCallback(async (): Promise<boolean> => {
    if (!dealId) return false;
    lastSaveAttemptRef.current = Date.now();
    setSaveState('saving');
    try {
      const body = { text, fileIds };
      await saveDraft(dealId, body);
      savedSnapshotRef.current = { text: body.text, fileIds: body.fileIds };
      setLastSavedAt(new Date());
      setSaveState('saved');
      return true;
    } catch (err) {
      toast.error(errorMessage(err, 'Không lưu được nháp, hệ thống sẽ thử lại.'));
      setSaveState('idle');
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, text, fileIdsKey]);

  /** Lưu nháp — nếu đã có 1 lượt lưu đang bay (autosave hoặc lượt gọi khác), TRẢ VỀ ĐÚNG promise đó
   * thay vì bắn thêm request song song (fix round 1 Important #3: "Gửi duyệt" bấm đúng lúc autosave
   * đang chạy không còn báo lỗi giả — nó chờ lượt đang chạy xong). */
  const doSave = useCallback((): Promise<boolean> => {
    if (inFlightSaveRef.current) return inFlightSaveRef.current;
    const p = performSave().finally(() => {
      inFlightSaveRef.current = null;
    });
    inFlightSaveRef.current = p;
    return p;
  }, [performSave]);

  /** Dùng trước khi nộp bài/rời trang — chờ lượt lưu đang bay (nếu có) xong, rồi lưu thêm 1 lần nữa
   * NẾU nội dung đã đổi tiếp sau đó (vd gõ thêm trong lúc autosave cũ đang gửi). */
  const flushSave = useCallback(async (): Promise<boolean> => {
    if (inFlightSaveRef.current) {
      const ok = await inFlightSaveRef.current;
      if (!ok) return false;
    }
    const changed =
      text !== savedSnapshotRef.current.text || fileIdsKey !== savedSnapshotRef.current.fileIds.join(',');
    return changed ? doSave() : true;
  }, [doSave, text, fileIdsKey]);

  // Tự lưu tối đa 1 lần mỗi 30 giây, chỉ khi nội dung thật sự đổi so với lần lưu gần nhất.
  useEffect(() => {
    if (!canEdit || !loadedOnceRef.current) return;
    const changed =
      text !== savedSnapshotRef.current.text ||
      fileIdsKey !== savedSnapshotRef.current.fileIds.join(',');
    if (!changed) return;

    const elapsed = Date.now() - lastSaveAttemptRef.current;
    const delay = Math.max(0, AUTOSAVE_INTERVAL_MS - elapsed);
    const timer = setTimeout(() => doSave(), delay);
    return () => clearTimeout(timer);
  }, [text, fileIdsKey, canEdit, doSave]);

  // Cờ "còn thay đổi chưa lưu" đọc được từ trong closure cũ (beforeunload/cleanup unmount bên dưới) —
  // cập nhật mỗi lần render, không cần effect riêng.
  const hasUnsavedRef = useRef(false);
  hasUnsavedRef.current =
    canEdit &&
    (text !== savedSnapshotRef.current.text || fileIdsKey !== savedSnapshotRef.current.fileIds.join(','));

  // Luôn gọi ĐÚNG hàm flushSave mới nhất từ cleanup unmount (ref — không đưa flushSave vào deps của
  // effect dưới, tránh effect đó chạy lại mỗi lần gõ chữ, vì mỗi lần như vậy sẽ lại tháo + gắn listener
  // và hiểu lầm là "component rời trang" ngay giữa lúc gõ).
  // Review M1 (final review): dùng flushSave (CHỜ lượt lưu đang bay xong rồi lưu thêm nếu nội dung đổi
  // tiếp sau đó), không phải doSave (chỉ trả về đúng promise đang bay, bỏ qua nội dung gõ thêm sau khi
  // nó đã bắt đầu) — tránh mất đúng phần gõ trong khoảng autosave cũ đang gửi dở.
  const flushSaveRef = useRef(flushSave);
  useEffect(() => {
    flushSaveRef.current = flushSave;
  }, [flushSave]);

  // Fix round 1 Important #4: cảnh báo trước khi đóng/rời tab khi còn thay đổi chưa lưu + lưu nốt lúc
  // rời trang trong app (route khác/unmount) — tránh mất nội dung gõ trong khoảng chưa tới mốc tự lưu.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!hasUnsavedRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Fire-and-forget: unmount cleanup không thể await, nhưng flushSave tự chờ lượt lưu đang bay
      // (nếu có) xong trước khi lưu thêm — đủ để không mất nội dung gõ cuối cùng.
      if (hasUnsavedRef.current) flushSaveRef.current();
      // Clear videoProcessingBlocked timer on unmount.
      if (videoProcessingBlockedTimerRef.current) {
        clearTimeout(videoProcessingBlockedTimerRef.current);
        videoProcessingBlockedTimerRef.current = null;
      }
    };
  }, []);

  // Đếm để kiểm trước khi gửi/tải thêm — file tải lên trong phiên này đã biết đúng loại ngay (phản hồi
  // upload trả `kind`); file nạp lại từ nháp cũ (trang load lại) có loại "unknown" tới khi vé xem trả về
  // (DraftFileThumb, vé nay kèm sẵn `kind` — xem fix round 1), tạm tính là ảnh trong lúc chờ.
  const imageCount = attachments.filter((a) => a.kind === 'image' || a.kind === 'unknown').length;
  const videoCount = attachments.filter((a) => a.kind === 'video').length;

  const handleKindResolved = (fileId: string, kind: 'image' | 'video') => {
    setAttachments((prev) => prev.map((a) => (a.fileId === fileId ? { ...a, kind } : a)));
  };

  const handleRemove = (fileId: string) => {
    setAttachments((prev) => prev.filter((a) => a.fileId !== fileId));
  };

  const onPickImages = async (files: FileList | null) => {
    if (!dealId || !files || files.length === 0) return;
    const remaining = BRAND_SUBMISSION_LIMITS.maxImages - imageCount;
    if (remaining <= 0) {
      toast.error('Tối đa 10 ảnh và 1 video.');
      return;
    }
    const picked = Array.from(files).slice(0, remaining);
    setUploadingImage(true);
    try {
      for (const file of picked) {
        if (!IMAGE_MIME_TYPES.includes(file.type)) {
          toast.error('Định dạng file không được hỗ trợ.');
          continue;
        }
        if (file.size > MAX_IMAGE_BYTES) {
          toast.error('Ảnh tối đa 10 MB.');
          continue;
        }
        setImageProgress(0);
        try {
          // Tải tuần tự (không song song) — tiến độ hiện đúng từng ảnh một, tránh dồn nhiều request
          // upload cùng lúc làm chậm cả hai.
          // eslint-disable-next-line no-await-in-loop
          const res = await uploadDraftFile(dealId, file, setImageProgress);
          setAttachments((prev) => [...prev, { fileId: res.fileId, kind: 'image' }]);
        } catch (err) {
          toast.error(errorMessage(err, 'Tải ảnh thất bại.'));
        }
      }
    } finally {
      setUploadingImage(false);
      setImageProgress(0);
    }
  };

  const onPickVideo = async (file?: File) => {
    if (!dealId || !file) return;
    if (videoCount >= BRAND_SUBMISSION_LIMITS.maxVideos) {
      toast.error('Tối đa 10 ảnh và 1 video.');
      return;
    }
    if (!VIDEO_MIME_TYPES.includes(file.type)) {
      toast.error('Định dạng file không được hỗ trợ.');
      return;
    }
    if (file.size > MAX_VIDEO_BYTES) {
      toast.error(VIDEO_TOO_LARGE_ERROR);
      return;
    }
    setUploadingVideo(true);
    setVideoProgress(0);
    try {
      const res = await uploadDraftFile(dealId, file, setVideoProgress);
      setAttachments((prev) => [...prev, { fileId: res.fileId, kind: 'video' }]);
    } catch (err) {
      toast.error(errorMessage(err, 'Tải video thất bại.'));
    } finally {
      setUploadingVideo(false);
      setVideoProgress(0);
    }
  };

  const handleSubmit = async () => {
    if (!dealId || !task) return;
    const error = validateDraft({ text, images: imageCount, videos: videoCount });
    if (error) {
      toast.error(error);
      return;
    }
    const formatError = validateContentFormat(task.contentFormat, text, imageCount, videoCount);
    if (formatError) {
      toast.error(formatError);
      return;
    }
    if (!window.confirm('Sau khi gửi, bản này bị khoá.')) return;

    setSubmitting(true);
    try {
      // Endpoint nộp bài không nhận text/fileIds — nó dùng đúng bản nháp đã lưu gần nhất, nên phải chờ
      // lượt tự lưu đang bay (nếu có) xong, rồi lưu thêm lần nữa nếu có gõ thêm sau đó, trước khi nộp.
      const saved = await flushSave();
      if (!saved) {
        toast.error('Không lưu được nháp mới nhất, thử lại.');
        return;
      }
      const updated = await submitTask(dealId);
      setTask(updated);
      toast.success('Đã gửi bài, chờ doanh nghiệp duyệt.');
      load();
    } catch (err) {
      const message = errorMessage(err, 'Không gửi được bài.');
      toast.error(message);
      // Review I4: video vừa tải lên có thể chưa kịp có preview (đóng dấu chạy nền — xem R15 bên
      // media-service) — payment từ chối nộp bài với đúng câu này; khoá nút tới khi creator đổi tệp
      // đính kèm, tránh bấm lại ngay và dính lại y hệt lỗi trong lúc preview vẫn còn đang xử lý.
      if (message.includes('đang được xử lý')) setVideoProcessingBlocked(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRate = async (stars: number, comment: string) => {
    if (!dealId) return;
    setRatingBusy(true);
    try {
      const result = await submitCreatorRating(dealId, stars, comment || undefined);
      toast.success('Đã gửi đánh giá.');
      setMyRating({ stars: result.stars, comment: result.comment, createdAt: result.createdAt });
    } catch (err) {
      toast.error(errorMessage(err, 'Không gửi được đánh giá.'));
    } finally {
      setRatingBusy(false);
    }
  };

  const handleAbandon = async () => {
    if (!dealId) return;
    if (!window.confirm('Bỏ nhiệm vụ này? Suất sẽ mở lại cho người khác, bạn không bị phạt.')) return;
    setAbandoning(true);
    try {
      await abandonTask(dealId);
      toast.success('Đã bỏ nhiệm vụ.');
      navigate(`${routePaths.brandDeals}?tab=tasks`);
    } catch (err) {
      toast.error(errorMessage(err, 'Không bỏ được nhiệm vụ này.'));
    } finally {
      setAbandoning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-10">
        <Loader2 className="animate-spin ui-muted" size={24} />
      </div>
    );
  }

  if (!task) {
    return <p className="ui-muted p-10 text-center text-[14px]">Không tìm thấy nhiệm vụ.</p>;
  }

  const showAbandon = canAbandon({ status: task.status, latestVersion: task.latestVersion });
  const saveLabel =
    saveState === 'saving' ? 'Đang lưu…' : lastSavedAt ? `Đã lưu lúc ${formatSavedAt(lastSavedAt)}` : '';

  return (
    <div className="mx-auto flex w-full max-w-[960px] flex-col gap-6 px-4 py-6 md:px-6">
      <div>
        <h1 className="ui-heading text-[20px] font-bold md:text-[24px]">{task.title}</h1>
        <p className="ui-muted text-[14px]">
          {task.brandName} <IndividualOrdererBadge kind={task.ordererKind} /> · {taskStatusLabel(task.status)}
        </p>
        {/* Đo chuyển đổi (spec 2026-10-05-brand-conversion §3) — lượt bấm, chuyển đổi, tỉ lệ. */}
        {stats && (
          <p className="ui-muted text-[13px]">
            {stats.clicks} lượt bấm · {stats.conversions} chuyển đổi · tỉ lệ {formatRate(stats.conversions, stats.clicks)}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-6 md:flex-row">
        {/* Trái: brief doanh nghiệp yêu cầu */}
        <div className="ui-card flex flex-col gap-3 p-4 md:w-[340px] md:shrink-0">
          <h2 className="ui-heading text-[15px] font-bold">{task.campaignKind === 'Proposal' ? 'Mong muốn của người đặt' : 'Brief'}</h2>
          {briefVersionLabel(brief?.version) && <span className="ui-muted text-[12px]">{briefVersionLabel(brief?.version)}</span>}
          {brief?.images && brief.images.length > 0 && (
            <div className="flex gap-2 overflow-x-auto">
              {brief.images.map((img) => (
                <img key={img.id} src={img.url} alt="" className="size-20 shrink-0 rounded-lg object-cover" />
              ))}
            </div>
          )}
          {brief && <p className="ui-text whitespace-pre-wrap text-[14px]">{brief.briefText}</p>}
          {brief?.destinationUrl && (
            <a
              href={brief.destinationUrl}
              target="_blank"
              rel="noreferrer"
              className="truncate text-[13px] font-semibold text-[#0057FF]"
            >
              {brief.destinationUrl}
            </a>
          )}
          <div className="ui-muted flex flex-col gap-1 text-[13px]">
            <span>{creativeRulesLabel(task.maxRevisions, task.reviewDays)}</span>
            <span>{THREAD_FAIRNESS_NOTE}</span>
            {contentFormatHint(task.contentFormat) && <span>{contentFormatHint(task.contentFormat)}</span>}
            {task.submitDeadline && <span>Hạn nộp bài: {countdownLabel(task.submitDeadline, Date.now())}</span>}
            {task.status === 'Submitted' && task.reviewDueAt && (
              <span>Doanh nghiệp còn hạn duyệt: {countdownLabel(task.reviewDueAt, Date.now())}</span>
            )}
            {task.status === 'Compensated' || dispute?.status === 'Upheld' ? (
              <span>
                Bạn được trả{' '}
                <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                  {/* Trọng tài quyết theo tỉ lệ: số thực nhận sau phí do payment tính; máy chủ cũ chưa trả thì là giá suất sau phí. */}
                  {Number(dispute?.creatorReceivedCa ?? task.netVnd ?? 0).toLocaleString('vi-VN')}đ
                </span>
              </span>
            ) : (
              <span>
                Bạn nhận <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                  {Number(task.netVnd || 0).toLocaleString('vi-VN')}đ
                </span>
              </span>
            )}
          </div>
        </div>

        {/* Phải: soạn bài hoặc xem lại */}
        <div className="flex flex-1 flex-col gap-4">
          {task.status === 'Invited' && <BriefInviteNotice />}
          {task.status === 'AwaitingPublish' && (
            <ProposalPublishBox dueAt={task.publishDueAt} nowMs={nowMs} busy={publishing} onPublish={handlePublish} />
          )}
          {task.status === 'RevisionRequested' && task.revisionNote && (
            <p className="rounded-lg bg-amber-50 p-3 text-[13px] text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
              Doanh nghiệp muốn bạn sửa bài: {task.revisionNote}
            </p>
          )}
          {task.status === 'Rejected' && (
            <p className="rounded-lg bg-rose-50 p-3 text-[13px] text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
              Doanh nghiệp đã từ chối bài: {rejectReasonLabel(task.rejectReasonCode)}
              {task.rejectReasonText ? ` — ${task.rejectReasonText}` : ''}
            </p>
          )}

          {dispute ? (
            <BrandDisputePanel
              dealId={dealId as string}
              dispute={dispute}
              viewerRole="Creator"
              nowMs={nowMs}
            />
          ) : showDisputeForm ? (
            <BrandDisputeForm
              dealId={dealId as string}
              versions={versions}
              onSubmitted={(d) => {
                setDispute(d);
                setShowDisputeForm(false);
                load();
              }}
              onCancel={() => setShowDisputeForm(false)}
            />
          ) : task?.disputeStatus ? (
            // Fix round 1: danh sách/đã biết có kháng cáo nhưng chi tiết kháng cáo chưa kịp nạp — hiện
            // đúng trạng thái thay vì CTA kháng cáo (tránh cho bấm gửi trùng).
            <p className="rounded-lg bg-amber-50 p-3 text-[13px] font-semibold text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
              Kháng cáo: {disputeStatusLabel(task.disputeStatus)}
            </p>
          ) : (
            canOpenDispute && (
              <div className="ui-card flex flex-wrap items-center justify-between gap-2 p-3">
                <span className="text-[13px] font-semibold text-amber-600 dark:text-amber-400">
                  {daysLeftLabel(disputeWindowEndsAt, nowMs)}
                </span>
                <button
                  type="button"
                  onClick={() => setShowDisputeForm(true)}
                  className="rounded-lg border border-rose-200 px-3 py-1.5 text-[13px] font-semibold text-rose-600 dark:border-rose-900/40"
                >
                  Báo cáo vi phạm / Kháng cáo bản quyền
                </button>
              </div>
            )
          )}

          {canEdit ? (
            <div className="ui-card flex flex-col gap-3 p-4">
              <label className="block">
                <textarea
                  value={text}
                  maxLength={BRAND_SUBMISSION_LIMITS.textMaxLength}
                  onChange={(e) => setText(e.target.value)}
                  rows={8}
                  placeholder="Viết nội dung bài theo đúng brief…"
                  className="w-full resize-none rounded-md border ui-divider bg-transparent px-3 py-2 text-[14px] outline-none ui-text"
                />
                <div className="mt-1 flex items-center justify-between text-[12px] ui-muted">
                  <span>{saveLabel}</span>
                  <span>
                    {text.length}/{BRAND_SUBMISSION_LIMITS.textMaxLength}
                  </span>
                </div>
              </label>

              <div className="flex flex-wrap gap-3">
                {attachments.map((a) => (
                  <DraftFileThumb
                    key={a.fileId}
                    dealId={dealId as string}
                    fileId={a.fileId}
                    kind={a.kind}
                    onKindResolved={handleKindResolved}
                    onRemove={() => handleRemove(a.fileId)}
                  />
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <label className="flex cursor-pointer items-center gap-1.5 rounded-lg border ui-divider px-3 py-2 text-[13px] font-semibold ui-text">
                  {uploadingImage ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
                  Thêm ảnh
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    hidden
                    disabled={uploadingImage}
                    onChange={(e) => {
                      onPickImages(e.target.files);
                      e.target.value = '';
                    }}
                  />
                </label>
                <label className="flex cursor-pointer items-center gap-1.5 rounded-lg border ui-divider px-3 py-2 text-[13px] font-semibold ui-text">
                  {uploadingVideo ? <Loader2 size={14} className="animate-spin" /> : <Video size={14} />}
                  Thêm video
                  <input
                    type="file"
                    accept="video/mp4,video/quicktime,video/webm"
                    hidden
                    disabled={uploadingVideo}
                    onChange={(e) => {
                      onPickVideo(e.target.files?.[0]);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>

              {uploadingImage && (
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                  <div className="h-full bg-[#0057FF] transition-all" style={{ width: `${imageProgress}%` }} />
                </div>
              )}
              {uploadingVideo && (
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                  <div className="h-full bg-[#0057FF] transition-all" style={{ width: `${videoProgress}%` }} />
                </div>
              )}

              <div className="flex flex-wrap gap-3">
                {showAbandon && (
                  <button
                    type="button"
                    disabled={abandoning || submitting || uploadingImage || uploadingVideo}
                    onClick={handleAbandon}
                    className="rounded-lg border ui-divider px-4 py-2 text-[14px] font-semibold ui-text disabled:opacity-60"
                  >
                    {abandoning ? 'Đang bỏ…' : 'Bỏ nhiệm vụ'}
                  </button>
                )}
                <button
                  type="button"
                  disabled={submitting || uploadingImage || uploadingVideo || videoProcessingBlocked}
                  onClick={handleSubmit}
                  className="ml-auto rounded-lg bg-[#0057FF] px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-60"
                >
                  {submitting ? 'Đang gửi…' : 'Gửi duyệt'}
                </button>
              </div>
            </div>
          ) : (
            <div className="ui-card flex flex-col gap-2 p-4">
              <h2 className="ui-heading text-[15px] font-bold">Bài đã nộp</h2>
              {text && <p className="ui-text whitespace-pre-wrap text-[14px]">{text}</p>}
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-3">
                  {attachments.map((a) => (
                    <DraftFileThumb
                      key={a.fileId}
                      dealId={dealId as string}
                      fileId={a.fileId}
                      kind={a.kind}
                      onKindResolved={handleKindResolved}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {versions.length > 0 && (
            <div className="flex flex-col gap-2">
              <h2 className="ui-heading text-[15px] font-bold">Các phiên bản đã nộp</h2>
              {[...versions].reverse().map((v) => (
                <VersionCard key={v.submissionId} version={v} />
              ))}
            </div>
          )}

          <BrandRatingBox
            ratingDueAt={ratingDueAt}
            myRating={myRating}
            theirRating={theirRating}
            counterpartLabel="Doanh nghiệp đánh giá bạn"
            busy={ratingBusy}
            onSubmit={handleRate}
          />

          {threadId && <BrandThreadPanel threadId={threadId} />}
        </div>
      </div>
    </div>
  );
};

/**
 * Trang làm việc `/collab/task/:dealId` cho mọi loại đơn (ruling R14): brief sáng tạo phía creator giữ bàn soạn
 * bài; mọi trường hợp khác (mời thẳng, ứng tuyển, đơn đặt quảng cáo, người đặt mở link) là đầu phòng và khung "Trao đổi".
 */
const BrandTaskWorkspacePage: React.FC = () => {
  const { dealId } = useParams<{ dealId: string }>();
  const [threadRef, setThreadRef] = useState<BrandThreadRef | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  // Hỏi phòng trao đổi lỗi nhưng người dùng là creator của một brief: vẫn mở bàn soạn bài, chỉ thiếu khung trao đổi.
  const [deskOnly, setDeskOnly] = useState(false);

  useEffect(() => {
    if (!dealId) {
      setFailure(NOT_FOUND_MESSAGE);
      return undefined;
    }
    let cancelled = false;
    setThreadRef(null);
    setFailure(null);
    setDeskOnly(false);
    getBrandThreadByDeal(dealId)
      .then((ref) => {
        if (!cancelled) setThreadRef(ref);
      })
      .catch((err) => {
        if (cancelled) return;
        if ((err as { response?: { status?: number } })?.response?.status === 404) {
          setFailure(NOT_FOUND_MESSAGE);
          return;
        }
        const message = errorMessage(err, 'Không mở được phòng trao đổi lúc này. Bạn thử lại sau nhé.');
        // Bàn soạn bài không được phụ thuộc vào phòng trao đổi: thử đọc nhiệm vụ của creator, được thì mở bàn soạn.
        getTaskDetail(dealId)
          .then(() => {
            if (!cancelled) setDeskOnly(true);
          })
          .catch(() => {
            if (!cancelled) setFailure(message);
          });
      });
    return () => {
      cancelled = true;
    };
  }, [dealId]);

  if (deskOnly && dealId) return <CreativeTaskWorkspace dealId={dealId} />;
  if (failure || !dealId) return <p className="ui-muted p-10 text-center text-[14px]">{failure ?? NOT_FOUND_MESSAGE}</p>;
  if (!threadRef) {
    return (
      <div className="flex justify-center p-10">
        <Loader2 className="animate-spin ui-muted" size={24} />
      </div>
    );
  }
  if (usesTaskFlow(threadRef.campaignKind) && threadRef.myRole === 'Creator') {
    return <CreativeTaskWorkspace dealId={dealId} threadId={threadRef.threadId} />;
  }

  const back =
    threadRef.myRole === 'Orderer'
      ? { to: routePaths.brandOrderer, label: 'Quay lại Thuê creator' }
      : { to: `${routePaths.brandDeals}?tab=deals`, label: 'Quay lại Sàn hợp tác sáng tạo' };
  return (
    <div className="mx-auto flex w-full max-w-[760px] flex-col gap-4 px-4 py-6 md:px-6">
      <Link to={back.to} className="self-start text-[14px] font-semibold text-[#0057FF]">
        {back.label}
      </Link>
      <BrandThreadPanel threadId={threadRef.threadId} showHeader />
    </div>
  );
};

export default BrandTaskWorkspacePage;
